import type { SignupSourceBucket } from "@atsready/shared";
import { format, parseISO } from "date-fns";
import type { BadgeVariant } from "@/components/ui/badge-variants";

/**
 * How admin shows a signup source: the one label map and the one pill look,
 * used by the Users column, the user detail row and the Signups by source card.
 *
 * The bucket rule itself is not here. Callers turn a stored value into a bucket
 * with `toSignupSourceBucket` from `@atsready/shared`, so `null`, `undefined`
 * and any stray text all land on `unknown` the same way the api counts them.
 */

export const SIGNUP_SOURCE_LABELS: Readonly<Record<SignupSourceBucket, string>> = {
  linkedin: "LinkedIn",
  x: "X",
  quora: "Quora",
  search: "Search",
  other: "Other",
  direct: "Direct",
  unknown: "Unknown",
};

export interface SignupSourceBadge {
  label: string;
  variant: BadgeVariant;
  className?: string;
}

/**
 * Every recorded source is a `secondary` pill, which holds contrast in both
 * themes; `unknown` is an outline pill in muted text so it reads as absence.
 */
export function signupSourceBadge(bucket: SignupSourceBucket): SignupSourceBadge {
  const label = SIGNUP_SOURCE_LABELS[bucket];
  if (bucket === "unknown") {
    return { label, variant: "outline", className: "text-muted-foreground" };
  }
  return { label, variant: "secondary" };
}

/** Bar fills on the Signups by source card (mockup M9). */
export const SIGNUP_SOURCE_BAR_CLASS: Readonly<Record<SignupSourceBucket, string>> = {
  linkedin: "bg-info",
  x: "bg-foreground",
  quora: "bg-primary",
  search: "bg-success",
  other: "bg-warning",
  direct: "bg-muted-foreground",
  unknown: "bg-muted-foreground/40",
};

export const UNKNOWN_SOURCE_EXPLANATION =
  "No source recorded: created before tracking, via the Chrome extension, or tags dropped as invalid.";

/**
 * The hint shown when every signup in the window is Unknown. `recordedSince` is
 * the api's ISO `YYYY-MM-DD` of the first account with a recorded source, or
 * `null` when no account has one yet.
 */
export function describeUnknownSourcesHint(recordedSince: string | null): string {
  if (recordedSince === null) {
    return "Sources are recorded only for accounts created after source tracking was switched on.";
  }
  return `Sources are recorded for accounts created from ${format(parseISO(recordedSince), "MMM d, yyyy")}.`;
}
